import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';

import {DrawerButtonTitle} from '../Common';
import {HeaderLayout, PageLayout} from '../../components';
import About from './About';

import {CurrentStateEnum, FastRobotErrorcode, Feedback} from '../../constants/dataEnum';
import withIntl from '../../lib/withIntl';

import {hideModal, showModal, setModalValid} from '../../stores/actions/modalStateAction';
import {hideLoading, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import {
  downgradeSoftware,
  showNewSoftwareUpdateAvailableModal,
  showSoftwareUpdateModal,
} from '../../stores/business/softwareUpdaterBusiness';
import {showFailedToDowngradeSystemModal} from '../../stores/business/modalStateBusinessAction';
import {showLicenseModal} from '../../stores/actions/licenceStateAction';
import {getInstrumentStatus} from '../../stores/business/instrumentBusiness';
import {showModalConnectionFailed} from '../../stores/business/commonModalBusiness';
import {showAutosaveConfirmationModal} from '../../stores/business/commonProtocolManagementBusiness';
import {saveProtocol} from '../../stores/business/protocolManagementBusiness';

const mapStateToProps = (state) => ({
  aboutState: state.aboutState,
  licenseState: state.licenseState,
  userManagementState: state.userManagementState,
  isPendingSaveExistState: state.protocolEditorState.isPendingSaveExist,
  autosaveState: state.protocolEditorState.autosave,
});

const mapDispatchToProps = (dispatch) => ({
  modalStateActionShowModal: bindActionCreators(showModal, dispatch),
  modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
  modalStateActionSetModalValid: bindActionCreators(setModalValid, dispatch),
  appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
  appStateActionHideLoading: bindActionCreators(hideLoading, dispatch),
  appStateActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  softwareUpdaterBusinessDowngradeSoftware: bindActionCreators(downgradeSoftware, dispatch),
  softwareUpdaterBusinessShowNewSoftwareUpdateAvailableModal: bindActionCreators(
    showNewSoftwareUpdateAvailableModal,
    dispatch,
  ),
  modalStateBusinessActionShowFailedToDowngradeSystemModal: bindActionCreators(
    showFailedToDowngradeSystemModal,
    dispatch,
  ),
  instrumentBusinessGetInstrumentStatus: bindActionCreators(getInstrumentStatus, dispatch),
  licenseStateActionShowLicenseModal: bindActionCreators(showLicenseModal, dispatch),
  onShowModalConnectionFailed: bindActionCreators(showModalConnectionFailed, dispatch),
  onShowSoftwareUpdateModal: bindActionCreators(showSoftwareUpdateModal, dispatch),
  commonProtocolManagementBusinessShowAutosaveConfirmationModal: bindActionCreators(
    showAutosaveConfirmationModal,
    dispatch,
  ),
});
function AboutContainer({
  aboutState,
  licenseState,
  userManagementState,
  isPendingSaveExistState,
  autosaveState,
  modalStateActionShowModal,
  modalStateActionHideModal,
  modalStateActionSetModalValid,
  appStateActionShowLoading,
  appStateActionHideLoading,
  appStateActionShowSnackbar,
  softwareUpdaterBusinessDowngradeSoftware,
  commonProtocolManagementBusinessShowAutosaveConfirmationModal,
  softwareUpdaterBusinessShowNewSoftwareUpdateAvailableModal,
  modalStateBusinessActionShowFailedToDowngradeSystemModal,
  instrumentBusinessGetInstrumentStatus,
  licenseStateActionShowLicenseModal,
  onShowModalConnectionFailed,
  onShowSoftwareUpdateModal,
  intl,
}) {
  const {softwareVersion, prevSWVersion} = aboutState;

  const {availableRoleRights} = userManagementState;

  const roleLevel = availableRoleRights.map((e) => e.roleLevel)[0];

  const isInstrumentIdle = async () => {
    appStateActionShowLoading(intl.formatMessage({id: 'instrument.loading.checkConnection'}));
    const response = await instrumentBusinessGetInstrumentStatus();
    appStateActionHideLoading();

    if (!response) {
      onShowModalConnectionFailed();
      return false;
    }

    if (response?.currentState !== CurrentStateEnum.IDLE) {
      appStateActionShowSnackbar(
        Feedback.ERROR,
        intl.formatMessage({
          id: 'about.updaterNotification.runningProtocol.downgrade',
        }),
      );
      modalStateActionSetModalValid();
      return false;
    }

    return true;
  };

  const downgradeHandler = async () => {
    const idle = await isInstrumentIdle();
    if (!idle) {
      return;
    }

    appStateActionShowLoading(
      <FormattedHTMLMessage id="about.updaterLoading.startApplyDowngrade.text" values={{version: prevSWVersion}} />,
    );
    const response = await softwareUpdaterBusinessDowngradeSoftware();
    if (response !== FastRobotErrorcode.NoError) {
      appStateActionHideLoading();
      modalStateBusinessActionShowFailedToDowngradeSystemModal();
    }
  };

  const showDowngradeModal = async () => {
    const idle = await isInstrumentIdle();
    if (!idle) {
      return;
    }

    modalStateActionShowModal(
      intl.formatMessage({id: 'about.downgradeModal.title'}),
      <FormattedHTMLMessage
        id="about.downgradeModal.content"
        values={{latestVersion: softwareVersion, olderVersion: prevSWVersion}}
      />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            modalStateActionHideModal();
            downgradeHandler();
          },
          text: intl.formatMessage({id: 'about.downgradeModal.firstButton'}),
          setButtonValidityByState: true,
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            modalStateActionHideModal();
          },
          text: intl.formatMessage({id: 'about.downgradeModal.secondButton'}),
        },
      ],
      () => modalStateActionHideModal(),
      '',
      true,
    );
  };

  const checkForPendingSaves = (callback) => {
    if (isPendingSaveExistState && !autosaveState) {
      commonProtocolManagementBusinessShowAutosaveConfirmationModal(null, true, saveProtocol, callback, callback);

      return;
    }

    callback();
  };

  const showActivateLicenseModal = async (isShow) => {
    appStateActionShowLoading(intl.formatMessage({id: 'instrument.loading.checkConnection'}));
    const response = await instrumentBusinessGetInstrumentStatus();
    appStateActionHideLoading();

    if (!response) {
      onShowModalConnectionFailed();
      return;
    }

    licenseStateActionShowLicenseModal(isShow);
  };

  return (
    <PageLayout>
      <PageLayout.Header>
        <HeaderLayout className="header">
          <HeaderLayout.Left>
            <DrawerButtonTitle title={intl.formatMessage({id: 'about.aboutHeader.titleMenu'})} />
          </HeaderLayout.Left>
        </HeaderLayout>
      </PageLayout.Header>
      <PageLayout.Content>
        <About
          logoIconSrc="./svg/login/fast-icon.svg"
          logoIconAlt="FAST Icon"
          logoTextSrc="./svg/login/fast-text.svg"
          logoTextAlt="Fast Liquid Handler"
          logoTessara="./svg/login/tessara.svg"
          logoTessaraAlt="Tessara"
          showSoftwareUpdateModal={() => checkForPendingSaves(onShowSoftwareUpdateModal)}
          showDowngradeModal={() => checkForPendingSaves(showDowngradeModal)}
          showUpdateAvailable={softwareUpdaterBusinessShowNewSoftwareUpdateAvailableModal}
          showActivateLicenseModal={showActivateLicenseModal}
          data={aboutState}
          licenseData={licenseState}
          userRoleLevel={roleLevel}
          isTessaraInstrument={licenseState.isTessaraInstrument}
        />
      </PageLayout.Content>
    </PageLayout>
  );
}

AboutContainer.propTypes = {
  aboutState: PropTypes.object.isRequired,
  licenseState: PropTypes.object.isRequired,
  userManagementState: PropTypes.object.isRequired,
  isPendingSaveExistState: PropTypes.object.isRequired,
  autosaveState: PropTypes.bool.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  appStateActionShowLoading: PropTypes.func.isRequired,
  appStateActionHideLoading: PropTypes.func.isRequired,
  modalStateActionSetModalValid: PropTypes.func.isRequired,
  appStateActionShowSnackbar: PropTypes.func.isRequired,
  softwareUpdaterBusinessDowngradeSoftware: PropTypes.func.isRequired,
  softwareUpdaterBusinessShowNewSoftwareUpdateAvailableModal: PropTypes.func.isRequired,
  commonProtocolManagementBusinessShowAutosaveConfirmationModal: PropTypes.func.isRequired,
  modalStateBusinessActionShowFailedToDowngradeSystemModal: PropTypes.func.isRequired,
  instrumentBusinessGetInstrumentStatus: PropTypes.func.isRequired,
  licenseStateActionShowLicenseModal: PropTypes.func.isRequired,
  onShowModalConnectionFailed: PropTypes.func.isRequired,
  onShowSoftwareUpdateModal: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(AboutContainer));
